import * as Sentry from '@sentry/nextjs';
import type { SP500Constituent } from 'utils/shared/sp500Constituents';
import {
  WIKIPEDIA_API_URL,
  MIN_EXPECTED_CONSTITUENTS,
  parseWikitextTable,
  isValidSP500Constituents,
} from 'utils/shared/sp500Constituents';

export {
  extractWikilink,
  extractTickerFromTemplate,
  parseWikitextTable,
  isValidSP500Constituents,
} from 'utils/shared/sp500Constituents';

/**
 * Fetch S&P 500 constituents from Wikipedia instead of FMP.
 * Returns the same SP500Constituent shape as the FMP endpoint.
 * Reports errors to Sentry but does not throw — returns [] on failure
 * so the caller can fall back to cached data.
 */
export async function fetchSP500ConstituentsFromWikipedia(): Promise<SP500Constituent[]> {
  try {
    const response = await fetch(WIKIPEDIA_API_URL);

    if (!response.ok) {
      const error = new Error(
        `Wikipedia API request failed: ${response.status} ${response.statusText}`,
      );
      Sentry.captureException(error, {
        tags: { source: 'wikipedia-sp500' },
        extra: { status: response.status },
      });
      return [];
    }

    const json = await response.json();
    const wikitext: string = json?.parse?.wikitext?.['*'];

    if (!wikitext) {
      const error = new Error('No wikitext found in Wikipedia API response');
      Sentry.captureException(error, {
        tags: { source: 'wikipedia-sp500' },
        extra: { responseKeys: Object.keys(json || {}) },
      });
      return [];
    }

    const constituents = parseWikitextTable(wikitext);
    const count = constituents.length;

    if (!isValidSP500Constituents(constituents)) {
      Sentry.captureMessage('Wikipedia returned an invalid S&P 500 constituent snapshot', {
        level: Sentry.Severity.Warning,
        tags: { source: 'wikipedia-sp500' },
        extra: { count, expectedMinimum: MIN_EXPECTED_CONSTITUENTS },
      });
      return [];
    }

    return constituents;
  } catch (error) {
    Sentry.captureException(error, {
      tags: { source: 'wikipedia-sp500' },
    });
    return [];
  }
}
